/**
 * Pourquoi ce paiement attend dans la file.
 *
 * L'écran affichait la file sans jamais dire pourquoi chaque ligne y était.
 * Or les raisons n'appellent pas du tout le même geste : une communication
 * structurée d'un autre système se classe hors périmètre en un clic et par
 * paquets, tandis qu'un virement sans référence demande une enquête.
 *
 * La raison n'est pas relue en base : elle se **déduit** de ce que la vue
 * `v_payment_allocation_state` porte déjà. C'est voulu — cela évite une
 * migration pour une information dérivable, et cela garde une seule vérité,
 * celle de `matching.ts`.
 *
 * ## Ce qu'elle ne peut PAS déduire seule, et que la file lui apporte
 *
 * Le 27 septembre 2026, un virement de 173,00 € attendait sous « communication
 * structurée valide mais inconnue de nos factures » alors que la communication
 * était celle de CDV-CSSA-2699901, facture déjà soldée — par le MÊME virement,
 * écrit une première fois par l'export CSV. La règle affirmait « inconnue »
 * sans avoir regardé : toute communication structurée restée en file passait
 * pour un autre système, et le bouton de classement par paquets la proposait.
 *
 * Deux faits lui sont donc apportés par la file, lus en base :
 *
 * - `factureStructuree` : la facture qui porte ces douze chiffres, si elle
 *   existe. `null` veut dire « cherchée, et aucune » ; seul ce cas-là est un
 *   autre système ;
 * - `jumeau` : la ligne de l'autre source (relevé CODA ↔ export CSV) de même
 *   forme, si elle existe. C'est la première raison de toutes : un virement lu
 *   deux fois ne se place pas, il se réunit.
 */

import { classifyOutOfScope } from './outOfScope';

export type QueueReasonCode =
  /** Une ligne de l'autre source décrit le même mouvement : lu deux fois. */
  | 'twin_suspected'
  /** Communication structurée d'une de NOS factures, que ce paiement n'a pas pu recevoir. */
  | 'own_structured'
  /** Communication structurée valide, mais inconnue de nos factures. */
  | 'foreign_structured'
  /** Une référence de facture a été lue, mais la facture n'a rien pu recevoir. */
  | 'reference_unusable'
  /** La forme trahit une activité tenue hors de cette comptabilité. */
  | 'out_of_scope_pattern'
  /** Aucune communication du tout. */
  | 'no_communication'
  /** Une communication, mais aucune référence de facture reconnaissable. */
  | 'unknown_reference'
  /** Le paiement a déjà été affecté en partie ; il reste un solde. */
  | 'partial_leftover';

export interface QueueReason {
  code: QueueReasonCode;
  /** Étiquette courte, pour la ligne de la file. */
  label: string;
  /** Phrase complète, pour le détail déplié. */
  hint: string;
  /**
   * Le classement hors périmètre est la suite probable, et peut se faire par
   * paquets. Sert à proposer la sélection multiple sans la deviner ligne à ligne.
   */
  probablyOutOfScope: boolean;
}

/** La facture qui porte la communication structurée du paiement. */
export interface FactureDeLaCommunication {
  numero: string;
  /** Ce qu'elle réclame encore, notes de crédit et encaissements déduits. */
  resteCents: number;
  annulee: boolean;
}

/** La ligne de l'autre source qui décrit peut-être le même mouvement. */
export interface JumeauEnFile {
  id: string;
  /** `bank` : le relevé CODA ; `bank_csv` : l'export CSV. */
  source: string;
  valueDate: string;
  communications: 'identiques' | 'une_absente' | 'differentes';
  /** Ce que cette autre ligne a déjà placé. */
  placeCents: number;
  /** Où, en clair : un numéro de facture, « provision », « hors périmètre ». */
  placeSur: string[];
}

export interface QueueReasonInput {
  amountCents: number;
  unallocatedCents: number;
  communicationRaw: string | null;
  structuredDigits: string | null;
  legacyInvoiceRef: string | null;
  /**
   * La facture de la communication structurée : `null` = cherchée, aucune ;
   * absente = pas cherchée. Seul `null` autorise à dire « un autre système ».
   */
  factureStructuree?: FactureDeLaCommunication | null;
  /** La ligne jumelle de l'autre source, s'il y en a une. */
  jumeau?: JumeauEnFile | null;
}

const NOM_DE_LA_SOURCE: Record<string, string> = {
  bank: 'le relevé CODA',
  bank_csv: "l'export CSV",
};

function eurosCourts(cents: number): string {
  return `${(cents / 100).toFixed(2).replace('.', ',')} €`;
}

export function queueReason(p: QueueReasonInput): QueueReason {
  /*
    Le virement lu deux fois passe avant TOUT, solde partiel compris : le
    placer, le mettre en provision ou le classer hors périmètre compterait
    deux fois un argent arrivé une fois. Mesuré le 27/09/2026 : 1 920,00 €
    ainsi offerts à une provision fictive, 2 981,80 € déjà classés deux fois.
  */
  if (p.jumeau) {
    const j = p.jumeau;
    const ou = NOM_DE_LA_SOURCE[j.source] ?? 'une autre source';
    const place = j.placeCents > 0
      ? ` Cette autre ligne est déjà placée (${eurosCourts(j.placeCents)}${j.placeSur.length ? ` sur ${j.placeSur.join(', ')}` : ''}).`
      : ' Cette autre ligne attend elle aussi.';
    const dit = j.communications === 'differentes'
      ? " Les deux communications diffèrent : deux virements distincts peuvent se ressembler à ce point — vérifiez sur l'extrait avant de réunir."
      : j.communications === 'identiques'
        ? ' Les deux portent la même communication.'
        : " L'une des deux ne porte aucune communication : rien ne les distingue.";
    return {
      code: 'twin_suspected',
      label: j.communications === 'differentes' ? 'lu deux fois ?' : 'lu deux fois',
      hint: `Même date valeur, même montant, même compte qu'un virement déjà enregistré par ${ou}.${dit}${place} C'est très probablement le même virement, lu une fois dans chaque fichier : ne le placez pas, ne le classez pas — réunissez les deux lignes.`,
      probablyOutOfScope: false,
    };
  }

  // Un solde après une affectation partielle passe avant tout le reste : ce
  // n'est pas un paiement mal lu, c'est un paiement à moitié placé.
  if (p.unallocatedCents > 0 && p.unallocatedCents < p.amountCents) {
    return {
      code: 'partial_leftover',
      label: 'solde à placer',
      hint: 'Ce paiement a déjà été affecté en partie. Il reste ce solde à placer — sur une autre facture, en provision, ou hors périmètre.',
      probablyOutOfScope: false,
    };
  }

  if (p.structuredDigits && p.factureStructuree) {
    const f = p.factureStructuree;
    const etat = f.annulee
      ? 'annulée'
      : f.resteCents <= 0 ? 'déjà soldée' : `qui attend encore ${eurosCourts(f.resteCents)}`;
    return {
      code: 'own_structured',
      label: f.annulee ? 'facture annulée' : f.resteCents <= 0 ? 'facture déjà soldée' : 'facture à vérifier',
      hint: `Communication structurée de la facture ${f.numero}, ${etat}. Ce paiement n'a pas pu la recevoir : double paiement de la famille, remboursement à prévoir, ou même virement enregistré deux fois. La recherche manuelle ci-dessous montre son solde exact. Ce n'est pas un autre système : ne le classez pas hors périmètre.`,
      probablyOutOfScope: false,
    };
  }

  if (p.structuredDigits && p.factureStructuree === undefined) {
    /* Pas cherchée — la lecture a échoué, ou l'appelant ne l'a pas faite. On
       ne dit donc ni « inconnue », ni « à classer par paquets ». */
    return {
      code: 'foreign_structured',
      label: 'communication structurée',
      hint: `Communication structurée valide (${p.structuredDigits}), que la file n'a pas pu comparer à nos factures. La recherche manuelle ci-dessous dit si elle en désigne une ; sans elle, ne la classez pas hors périmètre.`,
      probablyOutOfScope: false,
    };
  }

  if (p.structuredDigits) {
    return {
      code: 'foreign_structured',
      label: 'autre système',
      hint: `Communication structurée valide (${p.structuredDigits}) mais inconnue de nos factures : elle appartient très probablement à un autre système de l'association — garderie, parascolaire, études dirigées. Ces paiements se classent hors périmètre, et peuvent l'être par paquets.`,
      probablyOutOfScope: true,
    };
  }

  if (p.legacyInvoiceRef) {
    return {
      code: 'reference_unusable',
      label: 'facture inutilisable',
      hint: `La référence ${p.legacyInvoiceRef} a été lue, mais la facture n'a pas pu recevoir ce paiement : elle est annulée, déjà réglée, ou entièrement couverte par des notes de crédit. La recherche manuelle ci-dessous montre son solde exact. À décider à la main.`,
      probablyOutOfScope: false,
    };
  }

  const hors = classifyOutOfScope(p.communicationRaw);
  if (hors) {
    return {
      code: 'out_of_scope_pattern',
      label: hors.label,
      hint: `La communication a la forme d'une activité tenue hors de cette comptabilité (${hors.label}). Proposé, jamais appliqué : classer hors périmètre reste une décision comptable.`,
      probablyOutOfScope: true,
    };
  }

  if (!p.communicationRaw || p.communicationRaw.trim() === '') {
    return {
      code: 'no_communication',
      label: 'sans communication',
      hint: "Aucune communication. Reste le compte et le nom de l'émetteur : les suggestions ci-dessous partent de là.",
      probablyOutOfScope: false,
    };
  }

  return {
    code: 'unknown_reference',
    label: 'référence non reconnue',
    hint: "Une communication, mais aucune référence de facture reconnaissable. Les suggestions partent du nom et du compte ; la recherche manuelle permet de chercher par numéro de facture ou par nom de famille.",
    probablyOutOfScope: false,
  };
}
